import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Card,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import { REWARD_ACTIVITY_ID } from '@api/Rewards/RewardServ';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import * as Yup from 'yup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { Reward, RewardGoal } from '@api/Rewards/Reward';
import IOSSwitch from '@components/Utilities/Switch/IOSSwitch';
import RewardsService from '@api/Rewards/RewardCtrl';

const rewardsSchema = Yup.object({
    points: Yup.object().shape({
        name: Yup.string().required('Name is required'),
        cost: Yup.number().required('Cost is required'),
        rewardPoints: Yup.number().required('CashRegister is required')
    }),
    reward: Yup.object().shape({
        costPoints: Yup.number().required('Cost is required'),
        value: Yup.number().required('CashRegister is required')
    }),
    newUser: Yup.object().shape({
        status: Yup.boolean(),
        points: Yup.number().required('Points is required')
    })
});
const RewardsScreen: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const rewardsService = useMemo(() => new RewardsService(), []);
    const [goals, setGoals] = useState<RewardGoal[]>([
        {
            status: true,
            goalPoints: 10000,
            rewardPoints: 500
        },
        {
            status: true,
            goalPoints: 20000,
            rewardPoints: 1000
        }
    ]);

    const {
        control,
        handleSubmit,
        setValue,
        formState: { errors }
    } = useForm<Reward>({
        resolver: yupResolver(rewardsSchema)
    });
    const getData = async () => {
        showLoader();
        try {
            const reward = await rewardsService.getRewards();
            if (reward) {
                setValue('id', reward.id);
                setValue('points', reward.points);
                setValue('reward', reward.reward);
                setValue('newUser', reward.newUser);
                setGoals(reward.goals);
            }
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onSaveRewards: SubmitHandler<Reward> = async (values) => {
        showLoader();
        try {
            values.goals = goals;
            await rewardsService.saveReward(values);
            showAlertMessage('Recompensas guardadas exitosamente');
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    return (
        <Grid container height="100%" p={4}>
            <Grid item xs={6}>
                <Stack component={Card} boxShadow={4} borderRadius={4} p={4} height="100%">
                    <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="h5" fontWeight={600}>
                                Editar recompensas
                            </Typography>
                            {/* La bitácora del programa de puntos (M4). Esta pantalla
                                gobierna cuánto gana la cadena entera en cada compra y
                                hasta el 2026-09-11 se podía mover sin dejar rastro. */}
                            <Tooltip title="Ver quién cambió el programa de puntos">
                                <IconButton
                                    onClick={() =>
                                        dispatch(
                                            setActivityDrawer({
                                                isOpen: true,
                                                section: 'reward',
                                                sectionId: REWARD_ACTIVITY_ID
                                            })
                                        )
                                    }
                                >
                                    <HistoryIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <Typography variant="body2">Edita el programa de recompensas de KRISPIS</Typography>
                        <form id="save-rewards-form" onSubmit={handleSubmit(onSaveRewards)}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Controller
                                        name="points.name"
                                        control={control}
                                        defaultValue=""
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                autoFocus
                                                label="Nombre de los puntos"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.points?.name}
                                                helperText={errors.points?.name?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="points.cost"
                                        control={control}
                                        defaultValue={1}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                autoFocus
                                                label="Dinero gastado"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.points?.cost}
                                                helperText={errors.points?.cost?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="points.rewardPoints"
                                        control={control}
                                        defaultValue={1}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                autoFocus
                                                label="Puntos ganados"
                                                value={value}
                                                onChange={onChange}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">Puntos</InputAdornment>
                                                    )
                                                }}
                                                error={!!errors.points?.rewardPoints}
                                                helperText={errors.points?.rewardPoints?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6" fontWeight={600}>
                                        Editar recompensas
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="reward.value"
                                        control={control}
                                        defaultValue={5}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                autoFocus
                                                label="Descuento"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.reward?.value}
                                                helperText={errors.reward?.value?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="reward.costPoints"
                                        control={control}
                                        defaultValue={200}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                autoFocus
                                                label="Costo en puntos"
                                                value={value}
                                                onChange={onChange}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="start">Puntos</InputAdornment>
                                                    )
                                                }}
                                                error={!!errors.reward?.costPoints}
                                                helperText={errors.reward?.costPoints?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                                <Grid item xs={12}>
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Controller
                                            name="newUser.points"
                                            control={control}
                                            defaultValue={200}
                                            render={({ field: { onChange, value } }) => (
                                                <TextField
                                                    fullWidth
                                                    required
                                                    autoFocus
                                                    label="Puntos por nuevo usuario"
                                                    value={value}
                                                    onChange={onChange}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="start">Puntos</InputAdornment>
                                                        )
                                                    }}
                                                    error={!!errors.newUser?.points}
                                                    helperText={errors.newUser?.points?.message}
                                                />
                                            )}
                                        />
                                        <Controller
                                            name="newUser.status"
                                            control={control}
                                            defaultValue={true}
                                            render={({ field: { onChange, value } }) => (
                                                <IOSSwitch checked={value} onChange={onChange} />
                                            )}
                                        />
                                    </Stack>
                                </Grid>
                                {goals.map((goal, index) => {
                                    return (
                                        <Grid item xs={12} key={index}>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <TextField
                                                    fullWidth
                                                    required
                                                    autoFocus
                                                    label={`Puntos al acumullar ${goal.goalPoints} puntos`}
                                                    value={goal.rewardPoints}
                                                    onChange={(e) => {
                                                        const goalsArray = [...goals];
                                                        goalsArray[index].rewardPoints = Number(e.target.value);
                                                        setGoals(goalsArray);
                                                    }}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="start">Puntos</InputAdornment>
                                                        )
                                                    }}
                                                    error={!!errors.newUser?.points}
                                                    helperText={errors.newUser?.points?.message}
                                                />
                                                <IOSSwitch
                                                    checked={goal.status}
                                                    onChange={() => {
                                                        const goalsArray = [...goals];
                                                        goalsArray[index].status = !goalsArray[index].status;
                                                        setGoals(goalsArray);
                                                    }}
                                                />
                                            </Stack>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </form>
                        <Button color="primary" variant="contained" type="submit" form="save-rewards-form">
                            Guardar
                        </Button>
                    </Stack>
                </Stack>
            </Grid>
        </Grid>
    );
};
export default RewardsScreen;
